import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  ArrowRight,
  Shield,
  Loader2,
  Smartphone,
  Camera,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCoreController } from "@/services/coreController";
import { appApi } from "@/services/api";
import { toast } from "sonner";

const AppConstants = {
  sessionTimeoutMinutes: 3,
};

interface PaymentConfirmationState {
  type: "deposit";
  amount: number;
  accountType: string;
  accountId: string;
  accountName?: string;
  commercialId?: string;
  isAccountValid?: boolean;
  validatedUserName?: string;
}

export function PaymentConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as PaymentConfirmationState;
  const coreController = useCoreController();

  const [timeRemaining, setTimeRemaining] = useState<number>(
    AppConstants.sessionTimeoutMinutes * 60
  );
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [accountInfo, setAccountInfo] = useState<{
    userName: string;
    isLoading: boolean;
    error: string | null;
    isValid: boolean;
  }>({
    userName: "",
    isLoading: true,
    error: null,
    isValid: false,
  });
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!state) {
      navigate("/");
      return;
    }

    const fetchAccountInfo = async () => {
      if (state.accountId && !hasFetchedRef.current) {
        hasFetchedRef.current = true;
        try {
          setAccountInfo((prev) => ({ ...prev, isLoading: true, error: null }));
          const userName = await appApi.getUserName(
            state.accountId,
            coreController.compte.id
          );

          if (userName && userName.trim() !== "") {
            setAccountInfo({
              userName: userName,
              isLoading: false,
              error: null,
              isValid: true,
            });
          } else {
            setAccountInfo({
              userName: "",
              isLoading: false,
              error: "Identifiant de compte invalide",
              isValid: false,
            });
          }
        } catch (error: any) {
          let errorMessage = "Erreur lors de la récupération des informations";

          if (error.code === "ECONNABORTED") {
            errorMessage = "Délai d'attente dépassé - Veuillez réessayer";
          } else if (error.response?.status === 500) {
            errorMessage = "Service temporairement indisponible";
          } else if (error.response?.status === 404) {
            errorMessage = "Service non trouvé";
          } else if (!navigator.onLine) {
            errorMessage = "Connexion internet requise";
          }

          setAccountInfo({
            userName: "",
            isLoading: false,
            error: errorMessage,
            isValid: false,
          });
        }
      }
    };

    fetchAccountInfo();

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, state, coreController.compte.id]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleBack = () => {
    navigate("/deposit", {
      state: {
        type: "deposit",
        amount: state.amount,
        accountType: state.accountType,
        commercialId: state.commercialId,
      },
    });
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
    if (errors.phoneNumber) {
      setErrors((prev) => ({ ...prev, phoneNumber: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const phoneValidation = appApi.validatePhoneNumber(phoneNumber);
    if (!phoneValidation.valid) {
      newErrors.phoneNumber = phoneValidation.error || "Numéro invalide";
    }

    if (accountInfo.isLoading) {
      newErrors.account = "Veuillez attendre la validation du compte";
    } else if (!accountInfo.isValid) {
      newErrors.account = "Le compte n'a pas pu être validé";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validateForm()) {
      toast.error("Veuillez remplir tous les champs requis");
      return;
    }

    setIsLoading(true);

    try {
      console.debug("🚀 Proceeding with payment:", {
        phoneNumber,
        accountInfo,
        state,
      });

      toast.success("Paiement en cours de traitement...");
    } catch (error: any) {
      console.error("Error during payment:", error);
      toast.error("Une erreur s'est produite lors du paiement");
    } finally {
      setIsLoading(false);
    }
  };

  if (!state) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f0f4f8]">
      {/* Header */}
      <div className="px-4 py-4 flex items-center bg-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="text-gray-700 hover:bg-gray-100 rounded-full mr-3"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl font-bold text-[#133060]">
          Confirmation du paiement
        </h1>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 py-4 space-y-4">
        {/* Session Timer Pill */}
        <div className="flex justify-center">
          <div className="inline-flex items-center bg-white rounded-full px-5 py-2.5 shadow-sm border border-gray-200">
            <Clock className="w-4 h-4 text-[#133060] mr-2" />
            <span className="text-[#133060] text-sm font-medium">
              Session expire dans :{" "}
              <span className="font-bold">{formatTime(timeRemaining)}</span>
            </span>
          </div>
        </div>

        {/* Payment Info Card - Dark Blue */}
        <div className="bg-[#133060] rounded-2xl p-5">
          {/* Header row */}
          <div className="flex items-start justify-between mb-1">
            <div>
              <p className="text-gray-400 text-sm font-medium">
                Montant à Payer
              </p>
              <p className="text-white text-3xl font-bold mt-1">
                {state.amount.toLocaleString("fr-FR")} FCFA
              </p>
            </div>
            <button className="bg-white/10 hover:bg-white/20 transition-colors rounded-lg p-2.5">
              <Camera className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Info box */}
          <div className="bg-[#2B476F] rounded-xl px-4 py-3 mt-4">
            <p className="text-gray-300 text-sm leading-relaxed">
              Dépôt de {state.amount.toLocaleString("fr-FR")} FCFA sur le compte{" "}
              {state.accountId}
            </p>
            <p className="text-gray-300 text-sm mt-1">
              appartenant à{" "}
              <span className="text-[#e85a4f] font-bold text-xl">
                {accountInfo.isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin inline" />
                ) : accountInfo.userName ? (
                  accountInfo.userName
                ) : accountInfo.error ? (
                  <span className="text-red-400 text-base">{accountInfo.error}</span>
                ) : (
                  <span className="text-gray-400">Compte invalide</span>
                )}
              </span>
            </p>
          </div>
        </div>

        {/* Phone Number Card - White */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="text-[#133060] text-xl font-bold mb-1">
            Entrez votre numéro de téléphone
          </h2>
          <p className="text-gray-500 text-sm mb-5">
            Le numéro (ORANGE MONEY ou MOOV MONEY) que vous utiliserez pour
            effectuer le paiement
          </p>

          {/* Phone Input */}
          <div className="relative mb-5">
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <Smartphone className="w-5 h-5 text-gray-400" />
            </div>
            <Input
              type="tel"
              placeholder="ex. 76000000 ou 70000000"
              value={phoneNumber}
              onChange={(e) => handlePhoneNumberChange(e.target.value)}
              className={`w-full pl-12 pr-4 py-4 text-base border-2 rounded-xl bg-gray-50 ${
                errors.phoneNumber
                  ? "border-red-400 focus:border-red-500"
                  : "border-gray-200 focus:border-[#133060]"
              }`}
            />
          </div>

          {errors.phoneNumber && (
            <p className="text-sm text-red-500 mb-4">{errors.phoneNumber}</p>
          )}

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            disabled={
              !accountInfo.isValid ||
              accountInfo.isLoading ||
              !phoneNumber.trim() ||
              isLoading
            }
            className="w-full py-4 h-auto bg-[#133060] hover:bg-[#0f2944] text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                <span>Traitement...</span>
              </>
            ) : (
              <>
                <ArrowRight className="w-5 h-5 mr-2" />
                <span>Continuer</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PaymentConfirmationPage;
